import React, { useState, useEffect } from 'react';
import { getQuestions, createQuestion, updateQuestion, deleteQuestion } from '../../api';

export default function AdminBonusQuestions({ currentRace, setMessage }) {
    const [questions, setQuestions] = useState([]);
    const [newQ, setNewQ] = useState({ question_text: '', option_1: '', option_2: '', option_3: '', option_4: '', points: 1 });
    const [showExtraOptions, setShowExtraOptions] = useState(0);

    useEffect(() => {
        if (!currentRace) return;
        getQuestions(currentRace)
            .then((qs) => setQuestions(qs || []))
            .catch(() => setQuestions([]));
    }, [currentRace]);

    async function handleAddQuestion() {
        if (!currentRace) return setMessage('Select a race first');
        if (!newQ.question_text.trim() || !newQ.option_1.trim() || !newQ.option_2.trim()) {
            return setMessage('Question text and at least 2 options are required');
        }
        try {
            const created = await createQuestion({ ...newQ, race_id: currentRace });
            setQuestions((prev) => [...prev, created]);
            setNewQ({ question_text: '', option_1: '', option_2: '', option_3: '', option_4: '', points: 1 });
            setShowExtraOptions(0);
            setMessage('Question added');
        } catch (err) {
            console.error('Error adding question', err);
            setMessage('Error adding question');
        }
    }

    async function handleUpdateQuestion(q) {
        try {
            const updated = await updateQuestion(q.id, q);
            setQuestions((prev) => prev.map((x) => (x.id === q.id ? updated : x)));
            setMessage('Question updated');
        } catch (err) {
            console.error('Error updating question', err);
            setMessage('Error updating question');
        }
    }

    async function handleDeleteQuestion(id) {
        try {
            await deleteQuestion(id);
            setQuestions((prev) => prev.filter((x) => x.id !== id));
            setMessage('Question deleted');
        } catch (err) {
            console.error('Error deleting question', err);
            setMessage('Error deleting question');
        }
    }

    function handleQuestionFieldChange(id, field, value) {
        setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, [field]: value } : q)));
    }

    return (
        <>
            <hr className="my-4" />
            <h5>Bonus Questions</h5>
            {questions.length === 0 && <p className="text-muted">No bonus questions for this race yet.</p>}

            {questions.map((q, idx) => (
                <div key={q.id} className="card mb-3">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <strong>Q{idx + 1}</strong>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteQuestion(q.id)}>Delete</button>
                        </div>
                        <div className="mb-2">
                            <input className="form-control form-control-sm" placeholder="Question text" value={q.question_text}
                                onChange={(e) => handleQuestionFieldChange(q.id, 'question_text', e.target.value)} />
                        </div>
                        <div className="row g-2 mb-2">
                            <div className="col-6">
                                <input className="form-control form-control-sm" placeholder="Option 1" value={q.option_1}
                                    onChange={(e) => handleQuestionFieldChange(q.id, 'option_1', e.target.value)} />
                            </div>
                            <div className="col-6">
                                <input className="form-control form-control-sm" placeholder="Option 2" value={q.option_2}
                                    onChange={(e) => handleQuestionFieldChange(q.id, 'option_2', e.target.value)} />
                            </div>
                            <div className="col-6">
                                <input className="form-control form-control-sm" placeholder="Option 3 (optional)" value={q.option_3 || ''}
                                    onChange={(e) => handleQuestionFieldChange(q.id, 'option_3', e.target.value)} />
                            </div>
                            <div className="col-6">
                                <input className="form-control form-control-sm" placeholder="Option 4 (optional)" value={q.option_4 || ''}
                                    onChange={(e) => handleQuestionFieldChange(q.id, 'option_4', e.target.value)} />
                            </div>
                        </div>
                        <div className="row g-2 align-items-center">
                            <div className="col-auto">
                                <label className="form-label mb-0 small">Points:</label>
                            </div>
                            <div className="col-2">
                                <input type="number" className="form-control form-control-sm" min="1" value={q.points}
                                    onChange={(e) => handleQuestionFieldChange(q.id, 'points', parseInt(e.target.value) || 1)} />
                            </div>
                            <div className="col-auto">
                                <label className="form-label mb-0 small">Correct Answer:</label>
                            </div>
                            <div className="col-3">
                                <select className="form-select form-select-sm" value={q.correct_answer || ''}
                                    onChange={(e) => handleQuestionFieldChange(q.id, 'correct_answer', e.target.value ? parseInt(e.target.value) : null)}>
                                    <option value="">Not set</option>
                                    {q.option_1 && <option value="1">{q.option_1}</option>}
                                    {q.option_2 && <option value="2">{q.option_2}</option>}
                                    {q.option_3 && <option value="3">{q.option_3}</option>}
                                    {q.option_4 && <option value="4">{q.option_4}</option>}
                                </select>
                            </div>
                            <div className="col-auto">
                                <button className="btn btn-sm btn-primary" onClick={() => handleUpdateQuestion(q)}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            {/* Add New Question */}
            <div className="card border-dashed mb-3">
                <div className="card-body">
                    <h6>Add New Question</h6>
                    <div className="mb-2">
                        <input className="form-control form-control-sm" placeholder="Question text" value={newQ.question_text}
                            onChange={(e) => setNewQ({ ...newQ, question_text: e.target.value })} />
                    </div>
                    <div className="row g-2 mb-2">
                        <div className="col-6">
                            <input className="form-control form-control-sm" placeholder="Option 1 (required)" value={newQ.option_1}
                                onChange={(e) => setNewQ({ ...newQ, option_1: e.target.value })} />
                        </div>
                        <div className="col-6">
                            <input className="form-control form-control-sm" placeholder="Option 2 (required)" value={newQ.option_2}
                                onChange={(e) => setNewQ({ ...newQ, option_2: e.target.value })} />
                        </div>
                        {showExtraOptions >= 1 && (
                            <div className="col-6">
                                <input className="form-control form-control-sm" placeholder="Option 3 (optional)" value={newQ.option_3}
                                    onChange={(e) => setNewQ({ ...newQ, option_3: e.target.value })} />
                            </div>
                        )}
                        {showExtraOptions >= 2 && (
                            <div className="col-6">
                                <input className="form-control form-control-sm" placeholder="Option 4 (optional)" value={newQ.option_4}
                                    onChange={(e) => setNewQ({ ...newQ, option_4: e.target.value })} />
                            </div>
                        )}
                    </div>
                    <div className="d-flex gap-2 align-items-center mb-2">
                        {showExtraOptions < 2 && (
                            <button className="btn btn-sm btn-outline-secondary" type="button"
                                onClick={() => setShowExtraOptions((prev) => Math.min(prev + 1, 2))}>
                                + Add Option
                            </button>
                        )}
                        <label className="form-label mb-0 small ms-2">Points:</label>
                        <input type="number" className="form-control form-control-sm" min="1" style={{ width: 70 }} value={newQ.points}
                            onChange={(e) => setNewQ({ ...newQ, points: parseInt(e.target.value) || 1 })} />
                    </div>
                    <button className="btn btn-sm btn-success" type="button" onClick={handleAddQuestion}>Add Question</button>
                </div>
            </div>
        </>
    );
}
